"use client"

import type React from "react"
import { Link, useLocation } from "react-router-dom"
import { Users, Calendar, BarChart3, Sword, Moon, Sun, Trophy } from "lucide-react"
import { useTheme } from "../contexts/ThemeContext"

interface LayoutProps {
  children: React.ReactNode
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation()
  const { theme, toggleTheme } = useTheme()

  const navItems = [
    { path: "/", label: "Attendance", icon: Calendar },
    { path: "/fencers", label: "Fencers", icon: Users },
    { path: "/competitions", label: "Competitions", icon: Trophy },
    { path: "/statistics", label: "Statistics", icon: BarChart3 },
  ]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between">
            <div className="flex items-center justify-between py-3 sm:py-0">
              <div className="flex items-center">
                <Sword className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-3" />
                <span className="text-xl font-bold text-gray-900 dark:text-white">Fencer Management</span>
              </div>
              <div className="sm:hidden">
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-md text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                  aria-label="Toggle theme"
                >
                  {theme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div className="flex flex-row items-center justify-between sm:justify-end space-x-4 pb-3 sm:pb-0 overflow-x-auto">
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    location.pathname === path
                      ? "border-blue-500 text-gray-900 dark:text-white"
                      : "border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
                >
                  <Icon className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{label}</span>
                </Link>
              ))}
              <button
                onClick={toggleTheme}
                className="hidden sm:block p-2 rounded-md text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                aria-label="Toggle theme"
              >
                {theme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="py-4 sm:py-6">{children}</div>
      </main>
    </div>
  )
}
